// @ts-strict-ignore
import {AfterViewInit, Component, ElementRef, inject, input, OnInit, viewChild} from "@angular/core";
import {Translations} from "@translations";
import {FormControl, Validators} from "@angular/forms";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {DecimalPipe} from "@angular/common";
import {debounceTime} from "rxjs/operators";
import {SubscriptSizing} from "@angular/material/form-field";

@UntilDestroy()
@Component({
	selector: "sim-time-input",
	templateUrl: "./time-input.component.html",
	styleUrl: "./time-input.component.scss",
	providers: [DecimalPipe],
	standalone: false,
})
export class TimeInputComponent implements OnInit, AfterViewInit {
	// I/O
	public formCtrl = input.required<FormControl<number>>();
	public max = input<number | null, number>(null, {
		transform: (milliseconds: number | null) => Math.floor(milliseconds / 60_000),
	});
	public subScriptSizing = input<SubscriptSizing>("fixed");

	// ViewChildren
	public minInput = viewChild.required<ElementRef<HTMLInputElement>>("minInput");
	public secInput = viewChild.required<ElementRef<HTMLInputElement>>("secInput");

	// Properties
	public secCtrl = new FormControl<number>(0, [Validators.required, Validators.min(0), Validators.max(59)]);
	public minCtrl = new FormControl<number>(0, [Validators.required, Validators.min(0)]);

	// Readonly
	protected readonly T = Translations;

	// Injections
	private _number = inject(DecimalPipe);

	public ngOnInit(): void {
		this._convertPreselectedValue();
		this._duplicateParentErrors();
		this._observeValueChanges();
	}

	public ngAfterViewInit() {
		this.minInput().nativeElement.value = this._number.transform(this.minCtrl.value, "2.0-1");
		this.secInput().nativeElement.value = this._number.transform(this.secCtrl.value, "2.0-1");
	}

	private _convertPreselectedValue(): void {
		const valueInMs = this.formCtrl().value ?? 0;
		const valueInSeconds = valueInMs / 1000;
		const minutes = Math.floor(valueInSeconds / 60);
		const remainingSeconds = Math.floor(valueInSeconds % 60);
		this.minCtrl.setValue(minutes);
		this.secCtrl.setValue(remainingSeconds);
	}

	private _observeValueChanges(): void {
		const updateModel = (): void => {
			const minutes = this.minCtrl.value ?? 0;
			const seconds = this.secCtrl.value ?? 0;
			// Store as milliseconds
			this.formCtrl().setValue((minutes * 60 + seconds) * 1000, {emitModelToViewChange: false});
		};

		// View -> Model
		this.minCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
			updateModel();
			this.minInput().nativeElement.value = this._number.transform(value, "2.0-1");
		});
		this.secCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
			updateModel();
			this.secInput().nativeElement.value = this._number.transform(value, "2.0-1");
		});

		// Model -> View
		this.formCtrl()
			.valueChanges.pipe(untilDestroyed(this), debounceTime(100))
			.subscribe((value) => {
				const ms = value ?? 0;
				const totalSeconds = Math.floor(ms / 1000);
				// Min
				const minValue = Math.floor(totalSeconds / 60);
				this.minCtrl.setValue(minValue, {emitEvent: false});
				this.minInput().nativeElement.value = this._number.transform(minValue, "2.0-1");
				// Sec
				const secValue = totalSeconds % 60;
				this.secCtrl.setValue(secValue, {emitEvent: false});
				this.secInput().nativeElement.value = this._number.transform(secValue, "2.0-1");
			});
	}

	private _duplicateParentErrors(): void {
		this.formCtrl()
			.statusChanges.pipe(untilDestroyed(this))
			.subscribe((status) => {
				this.minCtrl.markAsTouched();
				this.minCtrl.setErrors(this.formCtrl().errors);

				if (status === "DISABLED") {
					this.minCtrl.disable({emitEvent: false});
					this.secCtrl.disable({emitEvent: false});
				}
			});
	}
}
